import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface Feature {
  title: string;
  description: string;
  icon: React.ReactNode;
  details?: string[];
}

interface MobileFeatureCarouselProps {
  features: Feature[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
  className?: string;
}

const GridPattern = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
    <div className="absolute inset-0 [mask-image:radial-gradient(black,transparent)]">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#111111_1px,transparent_1px),linear-gradient(to_bottom,#111111_1px,transparent_1px)] bg-[size:8px_8px]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#111111_1px,transparent_1px),linear-gradient(to_bottom,#111111_1px,transparent_1px)] bg-[size:32px_32px] opacity-50" />
      <div className="absolute inset-0 bg-[linear-gradient(45deg,#111111_1px,transparent_1px)] bg-[size:16px_16px]" />
      <div className="absolute inset-0 bg-[linear-gradient(-45deg,#111111_1px,transparent_1px)] bg-[size:16px_16px]" />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,76,0,0.03),transparent)]" />
      <div className="absolute inset-0 bg-[linear-gradient(0deg,transparent,rgba(255,76,0,0.03),transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_100%_200px,rgba(255,76,0,0.06),transparent)]" />
    </div>
  </div>
);

const MobileFeatureCarousel: React.FC<MobileFeatureCarouselProps> = ({ 
  features, 
  autoPlay = true, 
  autoPlayInterval = 5000,
  className = ""
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-play functionality
  useEffect(() => {
    if (!autoPlay || isPaused) return;

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          setCurrentIndex(current => (current + 1) % features.length);
          return 0;
        }
        return prev + (100 / (autoPlayInterval / 100));
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, [autoPlay, autoPlayInterval, features.length, isPaused]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsPaused(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsPaused(false);
      return;
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      setCurrentIndex(prev => (prev + 1) % features.length);
      setProgress(0);
    } else if (isRightSwipe) {
      setCurrentIndex(prev => (prev - 1 + features.length) % features.length);
      setProgress(0);
    }

    setTimeout(() => setIsPaused(false), 500);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setProgress(0);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 1000);
  };

  const currentFeature = features[currentIndex];

  return (
    <div className={`relative w-full ${className}`}>
      <div 
        className="touch-pan-x select-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ 
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1]
          }}
          className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border relative overflow-hidden p-6 min-h-[320px] shadow-lg"
          style={{ 
            backgroundImage: 'radial-gradient(circle at top right, rgba(255,76,0,0.1), transparent 50%)'
          }}
        >
          <GridPattern />
          <div className="relative flex flex-col items-center text-center h-full">
            <div className="text-primary-500 mb-6 transform transition-transform duration-300 hover:scale-110">
              {currentFeature.icon}
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
              {currentFeature.title}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
              {currentFeature.description}
            </p>
            {currentFeature.details && currentFeature.details.length > 0 && (
              <ul className="text-sm text-gray-500 dark:text-gray-500 space-y-1 mt-auto">
                {currentFeature.details.map((detail, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="w-1 h-1 bg-primary-500 rounded-full flex-shrink-0"></span>
                    {detail}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </motion.div>
      </div>

      {/* Progress indicators */}
      <div className="flex justify-center gap-2 mt-6">
        {features.map((_, index) => (
          <button
            key={index}
            className={`relative h-2 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-primary-500/20 w-8' 
                : 'bg-gray-300 dark:bg-gray-600 w-2 hover:bg-gray-400 dark:hover:bg-gray-500'
            }`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          >
            {index === currentIndex && (
              <div 
                className="absolute top-0 left-0 h-full bg-primary-500 rounded-full transition-all duration-100 ease-linear"
                style={{ 
                  width: `${progress}%`,
                }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Swipe indicator */}
      <div className="text-center mt-4">
        <p className="text-xs text-gray-400 dark:text-gray-600">
          Swipe left or right to navigate
        </p>
      </div>
    </div>
  );
};

export default MobileFeatureCarousel;
